<template>
  <div class="calendar-search">
    <div class="calendar-search__date">
      <h3 class="calendar-search__date-text">{{ formattedDate }}</h3>
      <div class="calendar-search__date-line"></div>
    </div>
    <div class="calendar-search__buttons">
      <SearchJourney
        :is-calendar="true"
        :button-text="$t('common.actions.navigate.previousDay')"
        :disabled="isCurrentDateToday"
        :show-outbound="showOutbound"
        :prev-icon="true"
        :nextRoute="{ name: 'SearchResultPage' }"
        @close="() => {}"
      />
      <SearchJourney
        :is-calendar="true"
        :button-text="$t('common.actions.navigate.nextDay')"
        :disabled="false"
        :show-outbound="showOutbound"
        :next-icon="true"
        :nextRoute="{ name: 'SearchResultPage' }"
        @close="() => {}"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { format, isToday, parseISO } from 'date-fns'
  import { nb, enGB } from 'date-fns/locale'
  import { format as formatInTimeZone } from 'date-fns-tz'
  import SearchJourney from './SearchJourney/SearchJourney.vue'
  import { getCurrentLocale } from '@/i18n'

  export default defineComponent({
    name: 'CalendarSearch',
    components: {
      SearchJourney,
    },
    props: {
      showOutbound: {
        type: Boolean,
        required: true,
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()

      const currentDate = computed(() => {
        return props.showOutbound
          ? journeyStore.departureDate
          : journeyStore.returnDate
      })

      const formattedDate = computed(() => {
        if (!currentDate.value || typeof currentDate.value !== 'string' || !currentDate.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return ''
        }
        
        try {
          const date = parseISO(currentDate.value)
          
          if (isNaN(date.getTime())) {
            return ''
          }
          
          const locale = getCurrentLocale() === 'no' ? nb : enGB
          const formatted = format(date, 'eeee dd. MMMM yyyy', { locale })
          return formatted.toLowerCase()
        } catch (error) {
          console.error('Error formatting date in CalendarSearch:', error)
          return ''
        }
      })

      const isCurrentDateToday = computed(() => {
        const date = parseISO(currentDate.value)
        const now = new Date()
        
        const getDateParts = (d: Date) => {
          const norwayOffset = 1
          const userOffset = -d.getTimezoneOffset() / 60
          const hoursDiff = norwayOffset - userOffset
          
          const adjustedDate = new Date(d)
          adjustedDate.setHours(d.getHours() + hoursDiff)
          
          return {
            year: adjustedDate.getFullYear(),
            month: adjustedDate.getMonth(),
            day: adjustedDate.getDate()
          }
        }
        
        const dateInNorway = getDateParts(date)
        const todayInNorway = getDateParts(now)
        
        return (
          dateInNorway.year === todayInNorway.year &&
          dateInNorway.month === todayInNorway.month &&
          dateInNorway.day === todayInNorway.day
        )
      })

      return {
        formattedDate,
        isCurrentDateToday,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .calendar-search {
    &__date {
      margin-bottom: 1rem;
    }

    &__date {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 0;

      &-text {
        @include title-medium;
        color: $clr-primary;
        white-space: nowrap;
      }

      &-line {
        width: 100%;
        border-bottom: 1px solid $clr-primary-100;
      }
    }

    &__buttons {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }
  }
</style>
